import { deleteConversations } from "@mirage/service-conversation";
import { removePreviousQueryFromCache } from "@mirage/service-typeahead-search";
import type { typeahead } from "@mirage/service-typeahead-search/service/types";
import Sentry from "@mirage/shared/sentry";
import { createContext, useCallback, useContext } from "react";

interface OmniboxResultRowContextValue {
  handleRemovePreviousQuery: (result: typeahead.ScoredPreviousQuery) => void;
  handleRemoveRecentChat: (result: typeahead.ScoredRecentChat) => void;
}

const OmniboxResultRowContext = createContext<
  OmniboxResultRowContextValue | undefined
>(undefined);

export function useOmniboxResultRowContext() {
  const context = useContext(OmniboxResultRowContext);
  if (context === undefined) {
    throw new Error(
      "useOmniboxResultRowContext must be used within a OmniboxResultRowProvider",
    );
  }
  return context;
}

interface OmniboxResultRowProviderProps {
  children: React.ReactNode;
  reloadResults: () => void;
}

export function OmniboxResultRowProvider({
  children,
  reloadResults,
}: OmniboxResultRowProviderProps) {
  const handleRemovePreviousQuery = useCallback(
    async (result: typeahead.ScoredPreviousQuery) => {
      try {
        await removePreviousQueryFromCache(result);
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        reloadResults();
      }
    },
    [reloadResults],
  );

  const handleRemoveRecentChat = useCallback(
    async (result: typeahead.ScoredRecentChat) => {
      try {
        await deleteConversations([result.result.serverConversationId]);
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    [],
  );

  return (
    <OmniboxResultRowContext.Provider
      value={{ handleRemovePreviousQuery, handleRemoveRecentChat }}
    >
      {children}
    </OmniboxResultRowContext.Provider>
  );
}
